/**
 * Server-side "no Karexia access" signal.
 *
 * The gateway (and every service) answers `403 {"error": "no_karexia_access"}`
 * for an authenticated shared-realm (`core`) account that holds no Karexia client
 * role. The API client calls `noteNoKarexiaAccess(status, body)` on every failed
 * response; when it matches, a module-level flag flips and subscribers (the
 * ProtectedRoute gate) re-render onto the no-access screen — even if the token's
 * own claims looked fine to the SPA (the server is authoritative).
 */

import { useSyncExternalStore } from 'react';

export const NO_KAREXIA_ACCESS_ERROR = 'no_karexia_access';
export const NO_KAREXIA_ACCESS_EVENT = 'psn:no-karexia-access';

let denied = false;
const listeners = new Set<() => void>();

/** True when a 403 body is the server's `no_karexia_access` refusal (flat or FastAPI `detail`). */
export function isNoKarexiaAccess(status: number, body: unknown): boolean {
  if (status !== 403 || !body || typeof body !== 'object') return false;
  const b = body as Record<string, unknown>;
  if (b.error === NO_KAREXIA_ACCESS_ERROR) return true;
  const d = b.detail;
  return !!d && typeof d === 'object' && !Array.isArray(d) && (d as Record<string, unknown>).error === NO_KAREXIA_ACCESS_ERROR;
}

/** Record a failed API response; flips the flag (and notifies) on a no-access refusal. */
export function noteNoKarexiaAccess(status: number, body: unknown): boolean {
  if (!isNoKarexiaAccess(status, body)) return false;
  if (!denied) {
    denied = true;
    listeners.forEach((l) => l());
    try {
      window.dispatchEvent(new CustomEvent(NO_KAREXIA_ACCESS_EVENT));
    } catch {
      /* non-browser test env */
    }
  }
  return true;
}

/** Clear the flag (after sign-out / account switch, or in tests). */
export function resetNoKarexiaAccess(): void {
  if (!denied) return;
  denied = false;
  listeners.forEach((l) => l());
}

export function isServerDeniedKarexiaAccess(): boolean {
  return denied;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** React hook: true once the server has refused this session with `no_karexia_access`. */
export function useServerDeniedKarexiaAccess(): boolean {
  return useSyncExternalStore(subscribe, isServerDeniedKarexiaAccess, isServerDeniedKarexiaAccess);
}
